import { useEffect, useRef, useState } from 'react'

import { CalcFormField } from '../../02-molecules/calculatorForm/Blok'
import CalculatorForm from '../../02-molecules/calculatorForm/CalculatorForm'
import { DownloadButton } from '../../02-molecules/calculatorForm/components/Results/Results'
import { PhnxThehulk } from '../../03-organisms/Thehulk/Thehulk'
import styles from './Calculator.module.css'

export type CalculatorProps = {
  headerTitle: string
  headerSubtitle: string
  headerDescription: any
  headerSubbanner: any
  headerAsset:
    | {
        src: string
        alt: string
        focus?: string
        wash_opacity?: number
      }
    | undefined
  calcFormFields: CalcFormField[]
  confirmationMessage: React.ReactNode
  ratings: any
  intro: React.ReactNode
  resultsKey: React.ReactNode
  disclaimer: React.ReactNode
  howitscalculated: React.ReactNode
}

export const PhnxCalculator: React.FC<CalculatorProps> = ({
  confirmationMessage,
  headerTitle,
  headerSubtitle,
  headerDescription,
  headerSubbanner,
  headerAsset,
  calcFormFields,
  ratings,
  intro,
  resultsKey,
  disclaimer,
  howitscalculated,
}) => {
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false)
  const [savedFormData, setSavedFormData] = useState(null)
  const [isGenerating] = useState(false)
  const downloadHandlerRef = useRef<(() => Promise<void>) | null>(null)

  const handleFormSubmmit = (data: any) => {
    setSavedFormData(data)
  }

  useEffect(() => {
    if (savedFormData !== null) {
      setFormSubmitted(true)
    }
  }, [savedFormData])

  // Separate effect for handling the download functionality
  useEffect(() => {
    const resultsDownloadButton = document.querySelector(
      '[data-testid="download-button"]'
    ) as HTMLButtonElement
    if (resultsDownloadButton) {
      downloadHandlerRef.current = async () => {
        resultsDownloadButton.click()
      }
    }
  }, [formSubmitted])

  // Effect for moving and setting up the button
  useEffect(() => {
    if (formSubmitted) {
      const messageFooter = document.querySelector('.Messagepanel_messagepanel__OZSLd')
      const downloadButton = document.querySelector('#download-results-button-print')?.parentElement

      if (messageFooter && downloadButton) {
        // Clone the button with its event listeners
        const newButton = downloadButton.cloneNode(true)
        messageFooter.appendChild(newButton)
        // Add click handler to the new button
        const button = (newButton as Element).querySelector('#download-results-button-print')
        if (button) {
          button.addEventListener('click', async () => {
            const resultsDownloadButton = document.querySelector(
              '#download-results-button'
            ) as HTMLButtonElement

            if (resultsDownloadButton) {
              // Update button state
              button.setAttribute('disabled', 'true')
              const buttonText = button.querySelector('.button__text')
              if (buttonText) {
                buttonText.textContent = 'Generating PDF...'
              }

              resultsDownloadButton.click()

              // Reset button state after download completes
              setTimeout(() => {
                button.removeAttribute('disabled')
                if (buttonText) {
                  buttonText.textContent = 'Download a copy'
                }
              }, 5000)
            } else {
              console.error('Original download button not found')
            }
          })
        }

        // Hide the original button
        downloadButton.style.display = 'none'
      }
    }
  }, [formSubmitted])

  return (
    <div className={styles['calculator']}>
      {formSubmitted && savedFormData !== null ? (
        <>
          <PhnxThehulk
            title={headerTitle}
            subtitle={headerSubtitle}
            description={headerDescription}
            asset={headerAsset}
            bannerContent={
              <>
                <div id="success-calc">
                  {confirmationMessage}
                  <div className={styles.confirmationButtons}>
                    <DownloadButton
                      onClick={() => {
                        const resultsDownloadButton = document.querySelector(
                          '[data-testid="download-button"]'
                        ) as HTMLButtonElement
                        if (resultsDownloadButton) {
                          resultsDownloadButton.click()
                        }
                      }}
                      isGenerating={isGenerating}
                      variant="print-only"
                    />
                  </div>
                </div>
              </>
            }
          />
          {intro}
          <CalculatorForm
            disclaimer={disclaimer}
            formFields={calcFormFields}
            ratings={ratings}
            initialFormData={savedFormData}
            showResults={true}
            printData={
              <div className={styles['printinfo']}>
                {howitscalculated}
                {resultsKey}
                {disclaimer}
              </div>
            }
          />
          <div className={`container ${styles['resultsinfo']}`}>
            {howitscalculated}
            {resultsKey}
            {disclaimer}
          </div>
        </>
      ) : (
        <PhnxThehulk
          title={headerTitle}
          subtitle={headerSubtitle}
          description={headerDescription}
          asset={headerAsset}
          bannerContent={
            <>
              <CalculatorForm
                disclaimer={disclaimer}
                formFields={calcFormFields}
                ratings={ratings}
                hideResultsScreen={true}
                onFormSubmit={handleFormSubmmit}
              />
            </>
          }
          subBannerContent={headerSubbanner}
        />
      )}
    </div>
  )
}
